import { StandardEvents } from '@shopify/events';

// Keep the current sale-price subtotal separate from the extra quantity saving.
export const hhnOfferAmounts = (selected, percentage) => {
  const before = selected.reduce((sum, variant) => sum + Math.max(0, Math.round(Number(variant?.price) || 0)), 0);
  const rawRate = Number(percentage);
  const rate = Number.isFinite(rawRate) ? Math.min(100, Math.max(0, rawRate)) : 0;
  const total = Math.round(before * (100 - rate) / 100);
  return { before, total, saving: before - total };
};

const initialized = new WeakSet();
const init = (root) => {
  if (!root || initialized.has(root)) return;
  initialized.add(root);
  const productDetails = root.closest('.product-details') || root.parentElement;
  const section = root.closest('.shopify-section') || productDetails;
  const form = () => productDetails.querySelector('product-form-component form[action*="/cart/add"]');
  const quantityInput = () => form()?.querySelector('[name="quantity"]');
  const offers = [...root.querySelectorAll('[data-hhn-offer]')];
  let variants = [];
  try { variants = JSON.parse(root.querySelector('[data-hhn-product-variants]')?.textContent || '[]'); } catch {}
  let activeVariant;
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: window.Shopify?.currency?.active || document.documentElement.getAttribute('data-shop-currency') || 'USD'
  }).format(cents / 100);
  const currentVariant = () => activeVariant || variants.find(v => String(v.id) === form()?.querySelector('[name="id"]')?.value) || variants[0];
  const quantity = () => Math.max(1, Number(quantityInput()?.value) || 1);
  const discountFor = (q) => Number(offers.find(o => Number(o.dataset.quantity) === Math.min(4, q))?.dataset.discount || 0);
  const panels = [...root.querySelectorAll('[data-hhn-mix]')];
  const variantById = id => variants.find(v => String(v.id) === String(id));
  const buildPanels = () => panels.forEach(panel => {
    if (panel.childElementCount) return;
    for (let i = 0; i < Number(panel.dataset.quantity); i++) {
      const row = document.createElement('label'); row.className = 'hhn-offer__mix-row';
      const number = document.createElement('span'); number.textContent = `#${i + 1}`;
      const select = document.createElement('select'); select.className = 'hhn-offer__mix-select';
      select.setAttribute('aria-label', `Item ${i + 1} option`);
      variants.forEach(v => { const option = document.createElement('option'); option.value = String(v.id); option.textContent = v.title || 'Default'; option.disabled = v.available === false; select.append(option); });
      select.value = String(currentVariant()?.id || variants.find(v => v.available)?.id || '');
      select.addEventListener('change', syncPrice);
      row.append(number, select); panel.append(row);
    }
  });
  const selectedFor = q => {
    const panel = panels.find(p => Number(p.dataset.quantity) === q);
    const ids = [...(panel?.querySelectorAll('select') || [])].map(select => select.value);
    return ids.length === q ? ids.map(id => variantById(id) || currentVariant()) : Array(q).fill(currentVariant());
  };
  const amountsFor = q => hhnOfferAmounts(selectedFor(q), discountFor(q));
  const totalFor = q => amountsFor(q).total;
  const syncPrice = () => {
    if (!root.isConnected) return;
    const variant = currentVariant();
    if (!variant) return;
    buildPanels();
    const q = quantity();
    offers.forEach(o => {
      const desired = Number(o.dataset.quantity);
      const input = quantityInput();
      const min = Number(input?.min) || 1;
      const max = input?.max ? Number(input.max) : Infinity;
      const step = Number(input?.step) || 1;
      o.disabled = variant.available === false || desired < min || desired > max || (desired - min) % step !== 0;
      const selected = desired === Math.min(q, 4);
      o.classList.toggle('is-selected', selected);
      o.setAttribute('aria-pressed', String(selected));
      o.setAttribute('aria-expanded', String(selected));
    });
    panels.forEach(panel => { panel.hidden = Number(panel.dataset.quantity) !== Math.min(q, 4); });
    const base = selectedFor(Math.min(q, 4));
    const chosen = q > 4 ? base.concat(Array(q - 4).fill(base.at(-1))) : base;
    const activeForm = form();
    if (activeForm) activeForm.dataset.hhnBundleIds = JSON.stringify(chosen.map(v => v.id));
    const express = productDetails.querySelector('product-form-component [ref="acceleratedCheckoutButtonContainer"]');
    if (express) express.hidden = chosen.some(v => String(v.id) !== String(activeForm?.querySelector('[name="id"]')?.value));
    root.querySelectorAll('[data-hhn-total]').forEach(el => el.textContent = money(totalFor(Number(el.dataset.quantity))));
    const subtotal = chosen.reduce((sum, v) => sum + Number(v?.price || 0), 0);
    const discounted = Math.round(subtotal * (100 - discountFor(q)) / 100);
    const compareTotal = chosen.reduce((sum, v) => sum + Number(v?.compare_at_price || v?.price || 0), 0);
    root.querySelectorAll('[data-hhn-tier-label]').forEach(el => {
      const tier=Number(el.dataset.quantity),rate=discountFor(tier);
      el.textContent=`Buy ${tier}${tier===4?'+':''}${rate>0?' · Extra '+rate+'% off':''}`;
    });
    root.querySelectorAll('[data-hhn-tier-note]').forEach(el => {
      const tier=Number(el.dataset.quantity),rate=discountFor(tier);
      el.textContent=rate>0?`Extra savings ${money(amountsFor(tier).saving)}`:'Automatic discounts calculated in cart';
    });
    root.querySelectorAll('[data-hhn-before-discount]').forEach(el => {
      const tier=Number(el.dataset.quantity),amounts=amountsFor(tier);
      el.textContent=money(amounts.before);
      el.hidden=discountFor(tier)<=0||amounts.saving<=0;
      el.setAttribute('aria-label',`Subtotal before quantity discount ${money(amounts.before)}`);
    });
    root.querySelectorAll('[data-hhn-total]').forEach(el => {
      el.setAttribute('aria-label',`${discountFor(Number(el.dataset.quantity))>0?'Estimated total':'Items subtotal'} ${el.textContent}`);
      el.title=discountFor(Number(el.dataset.quantity))>0?'Estimated total; final automatic discount calculated by Shopify in cart':'Items subtotal before automatic discounts';
    });
    let label=root.querySelector('[data-hhn-selection-total]');
    if(!label){label=document.createElement('p');label.dataset.hhnSelectionTotal='';label.className='hhn-selection-total';label.setAttribute('aria-live','polite');root.querySelector('.hhn-offers')?.after(label);}
    label.textContent=`${discountFor(q)>0?'Estimated total':'Items subtotal'} · ${q} ${q===1?'item':'items'}: ${money(discounted)}`;
    // The main product price stays the actual unit price; never overwrite it with a bundle estimate.

  };
  offers.forEach(offer => offer.addEventListener('click', () => {
    const input = quantityInput();
    if (!input) return;
    const desired = Number(offer.dataset.quantity);
    const max = input.max ? Number(input.max) : Infinity;
    const min = Number(input.min) || 1;
    if (desired > max || desired < min) { input.reportValidity(); return; }
    input.value = String(desired);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncPrice();
  }));
  productDetails.addEventListener('input', e => { if (e.target.matches('[name="quantity"]')) syncPrice(); });
  productDetails.addEventListener('change', e => { if (e.target.matches('[name="quantity"]')) syncPrice(); });
  section.addEventListener(StandardEvents.productSelect, event => {
    if (!root.isConnected || !productDetails.contains(event.target)) return;
    event.promise?.then(({detail}) => {
      if (detail?.productId && String(detail.productId) !== root.dataset.productId) return;
      if (detail?.resource?.id) {
        activeVariant = variantById(detail.resource.id) || detail.resource;
        panels.forEach(panel => { const first = panel.querySelector('select'); if (first && variantById(activeVariant.id)) first.value = String(activeVariant.id); });
      }
      requestAnimationFrame(syncPrice);
    }).catch(() => {});
  });
  form()?.addEventListener('submit', syncPrice);
  let cartRefreshTimer,cartInFlight=false;
  const cartStatus=root.querySelector('[data-hhn-cart-discount]');
  const refreshCart=async()=>{
    if(!root.isConnected||cartInFlight||document.visibilityState==='hidden')return;
    cartInFlight=true;
    try{
      const base=window.Shopify?.routes?.root||'/';
      const response=await fetch(base+'cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
      if(!response.ok)throw new Error('Cart unavailable');
      const cart=await response.json();
      if(!Array.isArray(cart.items)||!Number.isFinite(cart.total_price))throw new Error('Invalid cart');
      const applications=[...(cart.cart_level_discount_applications||[]),...cart.items.flatMap(item=>(item.line_level_discount_allocations||[]).map(a=>a.discount_application))].filter(Boolean);
      const titles=[...new Set(applications.map(a=>a.title).filter(Boolean))];
      if(cartStatus)cartStatus.textContent=cart.items.length?`Current cart subtotal: ${money(cart.total_price)}${titles.length?' · Applied: '+titles.join(' / '):' · No discount applied yet'}`:'';
    }catch{if(cartStatus)cartStatus.textContent='Final automatic discounts are calculated in your cart.';}
    finally{cartInFlight=false;}
  };
  const scheduleCart=()=>{clearTimeout(cartRefreshTimer);cartRefreshTimer=setTimeout(refreshCart,350);};
  const cleanup=new AbortController();
  document.addEventListener(StandardEvents.cartLinesUpdate,scheduleCart,{signal:cleanup.signal});
  window.addEventListener('focus',scheduleCart,{signal:cleanup.signal});
  window.addEventListener('pageshow',scheduleCart,{signal:cleanup.signal});
  document.addEventListener('visibilitychange',scheduleCart,{signal:cleanup.signal});
  const cartInterval=setInterval(()=>{if(!root.isConnected){clearInterval(cartInterval);clearTimeout(cartRefreshTimer);cleanup.abort();return;}refreshCart();},30000);
  refreshCart();

  syncPrice();
      const timer = root.querySelector('[data-hhn-timer]');
      if (!timer) return;
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
      const zonedTarget = (year, month, day, hour) => {
        const target = Date.UTC(year, month - 1, day, hour, 0, 0);
        let guess = target;
        for (let i = 0; i < 4; i++) {
          const values = Object.fromEntries(parts.formatToParts(new Date(guess)).map((part) => [part.type, part.value]));
          const represented = Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), Number(values.hour), Number(values.minute), Number(values.second));
          const correction = target - represented;
          if (!correction) break;
          guess += correction;
        }
        return guess;
      };
      const paintTimer = () => {
        const now = Date.now();
        const local = Object.fromEntries(parts.formatToParts(new Date(now)).map((part) => [part.type, part.value]));
        const year = Number(local.year), month = Number(local.month), day = Number(local.day), hour = Number(local.hour);
        const next = hour < 12 ? zonedTarget(year, month, day, 12) : zonedTarget(year, month, day + 1, 0);
        const remaining = Math.max(0, Math.floor((next - now) / 1000));
        const hours = Math.floor(remaining / 3600), minutes = Math.floor((remaining % 3600) / 60), seconds = remaining % 60;
        timer.querySelector('[data-hhn-hours]').textContent = String(hours).padStart(2, '0');
        timer.querySelector('[data-hhn-minutes]').textContent = String(minutes).padStart(2, '0');
        timer.querySelector('[data-hhn-seconds]').textContent = String(seconds).padStart(2, '0');
      };
      paintTimer();
      const timerInterval = window.setInterval(() => {
        if (!root.isConnected) { window.clearInterval(timerInterval); return; }
        paintTimer();
      }, 1000);
    };

const scan = () => document.querySelectorAll('[data-hhn-purchase-confidence]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, {once:true}); else scan();
document.addEventListener('shopify:section:load', scan);
